import { useEffect, useMemo, useState } from 'react';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useProfile } from '@/entities/profile';
import { useIntegrationsByAccountId } from '@/features/integrations/model/useIntegrationsByAccountId';
import { ConnectAmoCrmButton } from '@/features/integrations/ui/ConnectAmoCrmButton';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { Notification } from '@/shared/ui';

import { useAssignCompanyCrmIntegration } from '../model/useCompanyCrmIntegration';
import { useCompanyCrmRouting } from '../model/useCompanyCrmRouting';

const CRM_NAMES: Record<string, string> = {
  amocrm: 'amoCRM',
  bitrix24: 'Bitrix24',
  hubspot: 'HubSpot',
  pipedrive: 'Pipedrive',
  salesforce: 'Salesforce',
};

export const CompanyCrmIntegrationSettings = ({
  companyId,
}: {
  companyId: string;
}) => {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const { data: routing, isPending: isRoutingPending } =
    useCompanyCrmRouting(companyId);
  const { data: integrations, isPending: isIntegrationsPending } =
    useIntegrationsByAccountId(profile?.account_id);
  const assignMutation = useAssignCompanyCrmIntegration(companyId);
  const currentIntegrationId = routing?.integration?.integra_id ?? '';
  const [selectedIntegrationId, setSelectedIntegrationId] = useState('');

  useEffect(() => {
    setSelectedIntegrationId(currentIntegrationId);
  }, [currentIntegrationId]);

  const activeIntegrations = useMemo(
    () => (integrations ?? []).filter((integration) => integration.is_active),
    [integrations]
  );

  const handleSave = async () => {
    if (!selectedIntegrationId) return;
    await assignMutation.mutateAsync({ integra_id: selectedIntegrationId });
  };

  if (isRoutingPending || isIntegrationsPending) {
    return <Loader containerSize="small" />;
  }

  if (!activeIntegrations.length) {
    return (
      <NotFoundData
        title={t('company:crmIntegration.empty')}
        actionSlot={<ConnectAmoCrmButton />}
        sx={{
          height: 'auto',
          minHeight: { xs: 156, md: 86 },
          p: { xs: 2, md: 1.5 },
          borderRadius: 1,
          textAlign: 'center',
          gap: { xs: 1.25, md: 2 },
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'center',
          '& .MuiTypography-h5': {
            p: 0,
            fontSize: { xs: 20, md: 24 },
            lineHeight: 1.15,
            maxWidth: '100%',
          },
          '& .MuiTypography-subtitle1': {
            display: 'none',
          },
          '& .MuiButton-root': {
            whiteSpace: 'normal',
            minWidth: 0,
            flexShrink: 0,
          },
        }}
      />
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', xl: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', xl: 'flex-end' }}
        justifyContent="space-between"
      >
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={600}>
            {t('company:crmIntegration.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('company:crmIntegration.caption')}
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <FormControl
            size="small"
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 320 } }}
          >
            <InputLabel id="company-crm-integration-label">
              {t('company:crmIntegration.selectLabel')}
            </InputLabel>
            <Select
              labelId="company-crm-integration-label"
              value={selectedIntegrationId}
              label={t('company:crmIntegration.selectLabel')}
              onChange={(event) => setSelectedIntegrationId(event.target.value)}
            >
              {activeIntegrations.map((integration) => (
                <MenuItem key={integration.integra_id} value={integration.integra_id}>
                  {CRM_NAMES[integration.provider_type ?? ''] ?? 'CRM'} · {integration.crm_domain}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            sx={{ flexShrink: 0 }}
            disabled={
              !selectedIntegrationId ||
              selectedIntegrationId === currentIntegrationId ||
              assignMutation.isPending
            }
            onClick={handleSave}
          >
            {t('company:crmIntegration.save')}
          </Button>
        </Stack>
      </Stack>

      <Notification
        open={assignMutation.isError}
        title={assignMutation.error?.message}
      />
    </Box>
  );
};
